import Cookies from 'js-cookie';
import getAppUrl from './getAppUrl';

const ROOT_URL = getAppUrl();

/**
 * A utility for creating fetches to the Paperless Post API based on our old
 * Ajax request utility.
 *
 * The createFetch function is a manually curried utility so that we can partially
 * apply arguments to build more utilities (see some of the utility methods below)
 * to pass around the application. You will probably never need to use createFetch
 * directly.
 *
 * createFetchUtil -> createEndpointUtil -> endpointUtil
 *
 * createFetchUtil
 * @param {any} options - The fetch options object
 * @param {'GET' | 'POST' | 'PUT' | 'DELETE'} [options.method='GET'] - the http
 *     method to be used with this util.
 * @param {string} [options.responseType='json'] - Sets the response type of the
 *     request (for apis you probably want the default, 'json')
 * @param {boolean} [options.withCredentials=true] - whether or not this call
 *     should send auth cookies along with the request
 * @return {function} a function that will create a util of the given method
 *
 * @typedef {function} httpUtil
 * @param {any} options - The endpoint options object
 * @param {string[]?} options.allowedParams- the params (query string or post body)
 *     that the request should allow.
 * @param {string|function} options.path - Either a simple string mapping to an
 *     endpoint or a function that is passed the request params and must return
 *     a string. Allows you to inject params into the path.
 * @param {boolean?} options.csrf - Set to true to include the CSRF token stored
 *     in the cookie as a X-CSRF-Token header.
 * @param {string?} options.contentType - allows you to specify the contentType
 *     of the post/put payload. The fetch call will default to a form content-type,
 *     so if you want to use json, specify that here.
 * @param {any?} options.forcedParams - If you'd like to force a param to always
 *     be included with fetch calls, specify it here.
 * @return {function} the api util function that will fetch or send the data you request.
 *
 * @typedef {function} endpointUtil
 * @param {Object} params - the parameters of your request.
 * @param {Object} headers - any headers to be sent on your request.
 */

export interface FetchOptions {
  method: 'GET' | 'POST' | 'DELETE' | 'PUT';
  responseType?: string;
}

export type Path = string | PathFunction;

export type PathFunction = () => string;

export interface EndpointOptions {
  allowedParams?: string[];
  defaultParams?: any;
  path: Path;
  json?: boolean;
  csrf?: boolean;
  forcedParams?: any;
}

export function createFetch({ method = 'GET' }: FetchOptions = { method: 'GET' }) {
  return function httpUtil(
    { allowedParams = [], path, json = false, csrf = false, forcedParams = {} }: EndpointOptions = {
      path: '',
    }
  ) {
    return function endpointUtil(params: any = {}, headers: any = {}) {
      // @ts-expect-error
      const urlPath = `${ROOT_URL}${typeof path === 'function' ? path(params) : path}`;

      if (json) {
        headers['Content-Type'] = 'application/json';
      }

      if (csrf) {
        headers['X-CSRF-Token'] = window.decodeURIComponent(Cookies.get('_csrf_token') || '');
      }

      headers['X-Application'] = 'paper-browser-redux';

      let body = params;
      if (json) {
        if (allowedParams.length) {
          body = allowedParams.reduce((accum: any, ele: string) => {
            if (params[ele]) {
              accum[ele] = params[ele];
            }
            return accum;
          }, {});
        }

        body = JSON.stringify(Object.assign(body, forcedParams));
      }

      const url = urlPath;

      const props: RequestInit = {
        method,
        headers,
        credentials: 'include',
        body,
      };

      if (method === 'GET') {
        delete props.body;
      }

      return fetch(url, props);
    };
  };
}

/**
 * A utility for constructing GET requests
 * @type {httpUtil}
 */
export const createGet = createFetch({
  method: 'GET',
});

/**
 * A utility for constructing DELETE requests
 * @type {httpUtil}
 */
export const createDelete = createFetch({
  method: 'DELETE',
});

/**
 * A utility for constructing POST requests
 * @type {httpUtil}
 */
export const createPost = createFetch({
  method: 'POST',
});

/**
 * A utility for constructing POST requests
 * @type {httpUtil}
 */
export const createPut = createFetch({
  method: 'PUT',
});
